import { useState } from 'react';
import { EntityCard } from '@/components/EntityCard';
import { roleTemplatesPageMessages } from './roleTemplatesPage.messages';
import { fetchRoleTemplates } from '@/api/services/role-templates/fetchRoleTemplates';
import { softDeleteRoleTemplate } from '@/api/services/role-templates/softDeleteRoleTemplate';
import type { RoleTemplate } from 'schemas';
import { ApiError } from '@/api/ApiError';
import { UNKNOWN_ERROR_API_ERROR } from '@/api/helpers/unknownError';
import { Heading3, Muted } from '@/components/Typography';
import { DashboardPageContainer } from '@/components/layout/DashboardPageContainer';
import { CardsGridContainer } from '@/components/layout/CardsGridContainer';
import { transformRoleTemplateToEntityCard } from '@/pages/RoleTemplatesPage/helpers/transformRoleTemplateToEntityCard';
import { sharedMessages } from '@/shared-messages/shared.messages';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useLoadOnce } from '@/hooks/useLoadOnce';
import { Button } from '@/components/ui/button';
import { ROUTES } from '@/routes';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';

export function RoleTemplatesPage() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [roleTemplates, setRoleTemplates] = useState<RoleTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [deletingTemplateId, setDeletingTemplateId] = useState<string | null>(
    null
  );
  const [templateToDelete, setTemplateToDelete] = useState<RoleTemplate | null>(
    null
  );

  const handleDeleteTemplate = (template: RoleTemplate) => {
    setTemplateToDelete(template);
  };

  const confirmDeleteTemplate = async () => {
    if (!templateToDelete) return;

    try {
      setDeletingTemplateId(templateToDelete.id);
      await softDeleteRoleTemplate(templateToDelete.id);

      // Remove the deleted template from the local state
      setRoleTemplates((prevTemplates) =>
        prevTemplates.filter((template) => template.id !== templateToDelete.id)
      );

      toast.success(roleTemplatesPageMessages.deleteSuccess);
    } catch (err) {
      // Check if it's a validation error (role template in use)
      if (err instanceof ApiError && err.code === 'VALIDATION_ERROR') {
        toast.error(roleTemplatesPageMessages.deleteErrorInUse);
      } else {
        toast.error(roleTemplatesPageMessages.deleteErrorMessage);
      }
    } finally {
      setDeletingTemplateId(null);
      setTemplateToDelete(null);
    }
  };

  const cancelDeleteTemplate = () => {
    setTemplateToDelete(null);
  };

  useLoadOnce(() => {
    const loadRoleTemplates = async () => {
      try {
        setLoading(true);

        const fetchedTemplates = await fetchRoleTemplates();
        setRoleTemplates(fetchedTemplates);
      } catch (err) {
        const apiError =
          err instanceof ApiError ? err : UNKNOWN_ERROR_API_ERROR;
        toast.error(apiError.message);
      } finally {
        setLoading(false);
      }
    };

    loadRoleTemplates();
  });

  return (
    <DashboardPageContainer
      headline={roleTemplatesPageMessages.title}
      subheading={roleTemplatesPageMessages.subtitle}
      cta={
        <Button
          onClick={() => navigate(ROUTES.DASHBOARD_ROLE_TEMPLATES_CREATE)}
        >
          {roleTemplatesPageMessages.createButton}
        </Button>
      }
    >
      {roleTemplates.length === 0 && !loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <Heading3 className="mb-2">
              {roleTemplatesPageMessages.noTemplatesFound}
            </Heading3>
            <Muted>{roleTemplatesPageMessages.noTemplatesDescription}</Muted>
          </div>
        </div>
      ) : (
        <CardsGridContainer loading={loading}>
          {roleTemplates.map((template) => {
            const entityCardProps = transformRoleTemplateToEntityCard(template);
            const isDeleting = deletingTemplateId === template.id;
            return (
              <EntityCard
                key={template.id}
                {...entityCardProps}
                actions={
                  isAdmin
                    ? [
                        {
                          actionButtonLabel: isDeleting
                            ? sharedMessages.deleteFlow.deleting
                            : sharedMessages.commonButtons.delete,
                          onButtonClick: () => handleDeleteTemplate(template),
                        },
                      ]
                    : undefined
                }
              />
            );
          })}
        </CardsGridContainer>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={templateToDelete !== null}
        onOpenChange={cancelDeleteTemplate}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {roleTemplatesPageMessages.deleteConfirmTitle}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {roleTemplatesPageMessages.deleteConfirm}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelDeleteTemplate}>
              {sharedMessages.commonButtons.cancel}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteTemplate}
              disabled={deletingTemplateId !== null}
              className="bg-destructive hover:bg-destructive/90"
            >
              {deletingTemplateId === templateToDelete?.id
                ? sharedMessages.deleteFlow.deleting
                : sharedMessages.commonButtons.delete}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardPageContainer>
  );
}
